import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';

import {
	MACHINE_ACCESS_DAYS,
	MACHINE_ACCESS_ROLES,
	createMachineAccessProfile,
} from '../machineAccess';
import MachineAccessProfileEditor from './MachineAccessProfileEditor';

const DEFAULT_OPENING_WINDOW = '08:00-18:00';

const splitOpeningWindow = (value) => {
	const [opensAt = '', closesAt = ''] = (value || '').split('-');
	return { opensAt, closesAt };
};

const CollapsibleProfile = ({ profile, errors, recipeOptions, onChange, onDelete }) => {
	const [expanded, setExpanded] = useState(false);
	const roleLabel = profile.role === MACHINE_ACCESS_ROLES.USER ? 'User' : 'Operator';
	const hasErrors = Object.keys(errors).some((key) => key.startsWith(`${profile.clientId}.`));
	const error = (field) => errors[`${profile.clientId}.${field}`];
	const update = (field, fieldValue) => onChange({ ...profile, [field]: fieldValue });

	return (
		<div className={`machine-access-collapsible ${hasErrors ? 'machine-access-collapsible-error' : ''}`}>
			<div className="machine-access-collapsible-header">
				<button
					type="button"
					className="machine-access-collapsible-toggle"
					onClick={() => setExpanded((current) => !current)}
					aria-expanded={expanded}
					aria-controls={`${profile.clientId}-details`}
					aria-label={`${expanded ? 'Collapse' : 'Expand'} ${profile.name.trim() || `new ${roleLabel.toLowerCase()}`}`}
				>
					<i className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`}></i>
				</button>

				<div className="machine-access-summary-field machine-access-summary-name">
					<label htmlFor={`${profile.clientId}-summary-name`}>{roleLabel} name</label>
					<InputText
						id={`${profile.clientId}-summary-name`}
						value={profile.name}
						onChange={(event) => update('name', event.target.value)}
						maxLength={100}
						placeholder={`Enter ${roleLabel.toLowerCase()} name`}
						className={`w-full ${error('name') ? 'p-invalid' : ''}`}
					/>
					{error('name') && <small className="p-error">{error('name')}</small>}
				</div>

				<div className="machine-access-summary-field machine-access-summary-maximum">
					<label htmlFor={`${profile.clientId}-summary-maximum-recipes`}>Maximum recipes</label>
					<InputNumber
						inputId={`${profile.clientId}-summary-maximum-recipes`}
						value={profile.maximumRecipes}
						onValueChange={(event) => update('maximumRecipes', event.value)}
						min={0}
						maxFractionDigits={0}
						className={`w-full ${error('maximumRecipes') ? 'p-invalid' : ''}`}
					/>
					{error('maximumRecipes') && <small className="p-error">{error('maximumRecipes')}</small>}
				</div>

				<div className="machine-access-collapsible-actions">
					{hasErrors && <i className="pi pi-exclamation-circle p-error" aria-label="Profile has errors"></i>}
					<Button
						type="button"
						label="Delete"
						icon="pi pi-trash"
						severity="danger"
						text
						onClick={onDelete}
					/>
				</div>
			</div>
			{expanded && (
				<div id={`${profile.clientId}-details`} className="machine-access-collapsible-content">
					<MachineAccessProfileEditor
						profile={profile}
						errors={errors}
						recipeOptions={recipeOptions}
						onChange={onChange}
						onDelete={onDelete}
						summaryFieldsInHeader
					/>
				</div>
			)}
		</div>
	);
};

const MachineAccessSettings = ({
	section = 'general',
	value,
	validation,
	recipeOptions,
	loading,
	loadError,
	onChange,
	onRetry,
}) => {
	const [operatorSearch, setOperatorSearch] = useState('');
	const [userSearch, setUserSearch] = useState('');

	if (loading) {
		return (
			<div className="machine-access-loading">
				<ProgressSpinner style={{ width: '36px', height: '36px' }} strokeWidth="4" />
				<span className="text-sm text-600">Loading machine access settings...</span>
			</div>
		);
	}

	if (loadError || !value) {
		return (
			<div className="machine-access-load-error" role="alert">
				<div>
					<div className="font-medium text-700">Machine access settings could not be loaded</div>
					<small className="text-600">
						The rest of the machine settings remain available. Retry before changing access profiles.
					</small>
				</div>
				<Button type="button" label="Retry" icon="pi pi-refresh" outlined onClick={onRetry} />
			</div>
		);
	}

	const technician = value.profiles.find((profile) => profile.role === MACHINE_ACCESS_ROLES.TECHNICIAN);
	const operators = value.profiles.filter((profile) => profile.role === MACHINE_ACCESS_ROLES.OPERATOR);
	const users = value.profiles.filter((profile) => profile.role === MACHINE_ACCESS_ROLES.USER);
	const operatorQuery = operatorSearch.trim().toLocaleLowerCase();
	const userQuery = userSearch.trim().toLocaleLowerCase();
	const filteredOperators = operatorQuery
		? operators.filter((profile) => profile.name.toLocaleLowerCase().includes(operatorQuery))
		: operators;
	const filteredUsers = userQuery
		? users.filter((profile) => profile.name.toLocaleLowerCase().includes(userQuery))
		: users;

	const updateConfiguration = (field, fieldValue) => {
		onChange({
			...value,
			[field]: fieldValue,
		});
	};

	const updateProfile = (updatedProfile) => {
		updateConfiguration(
			'profiles',
			value.profiles.map((profile) => {
				if (profile.clientId !== updatedProfile.clientId) {
					return profile;
				}
				return updatedProfile.role === MACHINE_ACCESS_ROLES.TECHNICIAN
					? { ...updatedProfile, pin: profile.pin }
					: updatedProfile;
			}),
		);
	};

	const addProfile = (role) => {
		if (role === MACHINE_ACCESS_ROLES.OPERATOR) {
			setOperatorSearch('');
		} else if (role === MACHINE_ACCESS_ROLES.USER) {
			setUserSearch('');
		}
		updateConfiguration('profiles', [...value.profiles, createMachineAccessProfile(role)]);
	};

	const deleteProfile = (profileToDelete) => {
		updateConfiguration(
			'profiles',
			value.profiles.filter((profile) => profile.clientId !== profileToDelete.clientId),
		);
	};

	const updateSchedule = (field, fieldValue) => {
		updateConfiguration('orderingSchedule', {
			...value.orderingSchedule,
			[field]: fieldValue,
		});
	};

	const updateScheduleTime = (day, boundary, fieldValue) => {
		const currentWindow = splitOpeningWindow(value.orderingSchedule[day]);
		const nextWindow = {
			...currentWindow,
			[boundary]: fieldValue,
		};
		const nextValue =
			nextWindow.opensAt || nextWindow.closesAt ? `${nextWindow.opensAt}-${nextWindow.closesAt}` : '';
		updateSchedule(day, nextValue);
	};

	if (section === 'operators' || section === 'users') {
		const isOperatorSection = section === 'operators';
		const role = isOperatorSection ? MACHINE_ACCESS_ROLES.OPERATOR : MACHINE_ACCESS_ROLES.USER;
		const roleLabel = isOperatorSection ? 'Operator' : 'User';
		const roleLabelPlural = isOperatorSection ? 'Operators' : 'Users';
		const profiles = isOperatorSection ? filteredOperators : filteredUsers;
		const allProfiles = isOperatorSection ? operators : users;
		const search = isOperatorSection ? operatorSearch : userSearch;
		const setSearch = isOperatorSection ? setOperatorSearch : setUserSearch;

		return (
			<div className="machine-access-settings">
				<div className="flex align-items-center justify-content-between gap-3 mb-4">
					<div>
						<h4 className="text-xs font-semibold text-500 uppercase mt-0 mb-2 tracking-wider">
							{roleLabelPlural}
						</h4>
						<small className="text-sm text-600">
							{isOperatorSection
								? 'Multiple named operators may each have a unique PIN and individual access limits.'
								: 'Users enter only their unique PIN on the POS; no username is selected there.'}
						</small>
					</div>
					<Button
						type="button"
						label={`Add ${roleLabel.toLowerCase()}`}
						icon="pi pi-plus"
						outlined
						disabled={!value.enabled}
						onClick={() => addProfile(role)}
					/>
				</div>

				{!value.enabled ? (
					<div className="machine-access-disabled-note">
						<i className="pi pi-info-circle"></i>
						<span>
							Enable operator and user access on the Machine access tab before configuring {roleLabelPlural.toLowerCase()}.
						</span>
					</div>
				) : (
					<>
						<span className="p-input-icon-left machine-access-search mb-3">
							<i className="pi pi-search"></i>
							<InputText
								id={`machine-access-${roleLabel.toLowerCase()}-search`}
								value={search}
								onChange={(event) => setSearch(event.target.value)}
								placeholder={`Search ${roleLabelPlural.toLowerCase()} by name`}
								aria-label={`Search ${roleLabelPlural.toLowerCase()} by name`}
							/>
						</span>
						{allProfiles.length === 0 ? (
							<div className="machine-access-empty">No {roleLabelPlural.toLowerCase()} configured.</div>
						) : profiles.length === 0 ? (
							<div className="machine-access-empty">
								No {roleLabelPlural.toLowerCase()} match “{search.trim()}”.
							</div>
						) : (
							profiles.map((profile) => (
								<CollapsibleProfile
									key={profile.clientId}
									profile={profile}
									errors={validation.errors}
									recipeOptions={recipeOptions}
									onChange={updateProfile}
									onDelete={() => deleteProfile(profile)}
								/>
							))
						)}
					</>
				)}
			</div>
		);
	}

	return (
		<div className="machine-access-settings">
			<div className="flex flex-column md:flex-row md:align-items-start md:justify-content-between gap-3 mb-4">
				<div>
					<h4 className="text-xs font-semibold text-500 uppercase mt-0 mb-2 tracking-wider">Machine Access</h4>
					<p className="text-sm text-600 m-0">
						Configure PIN access for the POS. The technician stays available for hidden configuration access even
						when optional operator and user access is disabled.
					</p>
				</div>
				<div className="machine-access-toggle flex align-items-center justify-content-between gap-4 p-3 surface-50 border-round">
					<div className="machine-access-toggle-description">
						<label htmlFor="machine-access-enabled" className="text-sm font-medium text-700 block">
							Operator and user access
						</label>
						<small className="text-xs text-500">
							{value.enabled
								? 'The POS requires a configured PIN for optional access.'
								: 'Customer ordering stays in normal mode; technician access remains active.'}
						</small>
					</div>
					<InputSwitch
						className="machine-access-enabled-switch"
						inputId="machine-access-enabled"
						checked={value.enabled}
						onChange={(event) => updateConfiguration('enabled', event.value)}
					/>
				</div>
			</div>

			{validation.globalErrors.map((message) => (
				<div key={message} className="p-error mb-3" role="alert">
					{message}
				</div>
			))}

			<div className="mb-5">
				<div className="flex align-items-center gap-2 mb-3">
					<i className="pi pi-wrench text-primary"></i>
					<h5 className="text-sm font-semibold text-700 m-0">Required technician</h5>
				</div>
				{technician && (
					<MachineAccessProfileEditor
						profile={{ ...technician, pin: '' }}
						hasStoredPin={Boolean(technician.profileId)}
						errors={validation.errors}
						recipeOptions={recipeOptions}
						onChange={updateProfile}
					/>
				)}
			</div>

			<div className="machine-access-general mb-5">
				<div className="flex align-items-center gap-2 mb-3">
					<i className="pi pi-shield text-primary"></i>
					<h5 className="text-sm font-semibold text-700 m-0">PIN attempt protection</h5>
				</div>
				<div className="grid">
					<div className="col-12 lg:col-6">
						<div className="flex flex-column gap-2">
							<label htmlFor="failed-pin-lockout-minutes" className="text-sm font-medium text-700">
								Lockout after three failed PIN attempts
							</label>
							<InputNumber
								inputId="failed-pin-lockout-minutes"
								value={value.failedPinLockoutMinutes}
								onValueChange={(event) =>
									updateConfiguration('failedPinLockoutMinutes', event.value)
								}
								min={1}
								maxFractionDigits={0}
								suffix=" min"
								className={`w-full ${validation.errors.failedPinLockoutMinutes ? 'p-invalid' : ''}`}
							/>
							<small className="text-xs text-500">
								After three consecutive invalid PIN entries, the POS refuses all PINs for this many
								minutes. This also protects technician access when optional access is disabled.
							</small>
							{validation.errors.failedPinLockoutMinutes && (
								<small className="p-error">{validation.errors.failedPinLockoutMinutes}</small>
							)}
						</div>
					</div>
				</div>
			</div>

			{value.enabled && (
				<div className="machine-access-general mb-5">
						<div className="flex align-items-center gap-2 mb-3">
							<i className="pi pi-clock text-primary"></i>
							<h5 className="text-sm font-semibold text-700 m-0">General access timing</h5>
						</div>

						<div className="grid">
							<div className="col-12 lg:col-6">
								<div className="flex flex-column gap-2">
									<label htmlFor="recipe-allowance-reset-time" className="text-sm font-medium text-700">
										Recipe allowance reset time
									</label>
									<InputText
										id="recipe-allowance-reset-time"
										inputMode="numeric"
										maxLength={5}
										placeholder="HH:mm"
										value={value.recipeAllowanceResetTime}
										onChange={(event) =>
											updateConfiguration('recipeAllowanceResetTime', event.target.value)
										}
										className={`w-full ${
											validation.errors.recipeAllowanceResetTime ? 'p-invalid' : ''
										}`}
									/>
									<small className="text-xs text-500">
										Machine-local time in 24-hour HH:mm format when all user recipe allowances are restored.
									</small>
									{validation.errors.recipeAllowanceResetTime && (
										<small className="p-error">{validation.errors.recipeAllowanceResetTime}</small>
									)}
								</div>
							</div>

							<div className="col-12 lg:col-6">
								<div className="flex align-items-center justify-content-between gap-3 p-3 surface-50 border-round h-full">
									<div>
										<label htmlFor="ordering-schedule-enabled" className="text-sm font-medium text-700 block">
											Restrict ordering to opening hours
										</label>
										<small className="text-xs text-500">
											When enabled, ordering is available only inside the machine-local windows below.
										</small>
									</div>
									<InputSwitch
										inputId="ordering-schedule-enabled"
										className="machine-access-ordering-schedule-switch"
										checked={value.orderingSchedule.enabled}
										onChange={(event) => updateSchedule('enabled', event.value)}
									/>
								</div>
							</div>
						</div>

						{value.orderingSchedule.enabled && (
							<div className="machine-access-schedule mt-3">
								<div className="machine-access-schedule-header text-xs font-medium text-500 uppercase">
									<span>Day</span>
									<span>Open</span>
									<span>Close</span>
								</div>
								{MACHINE_ACCESS_DAYS.map(({ key, label }, dayIndex) => {
									const window = value.orderingSchedule[key];
									const { opensAt, closesAt } = splitOpeningWindow(window);
									const dayEnabled = Boolean(window);
									const previousEnabledDay = MACHINE_ACCESS_DAYS.slice(0, dayIndex)
										.reverse()
										.find(({ key: previousKey }) => Boolean(value.orderingSchedule[previousKey]));

									return (
										<div key={key} className="machine-access-schedule-row">
											<div className="machine-access-schedule-day">
												<div className="flex align-items-center gap-3">
													<InputSwitch
														inputId={`ordering-schedule-${key}`}
														checked={dayEnabled}
														onChange={(event) =>
															updateSchedule(key, event.value ? DEFAULT_OPENING_WINDOW : '')
														}
													/>
													<label htmlFor={`ordering-schedule-${key}`} className="text-sm font-medium text-700">
														{label}
													</label>
												</div>
												{dayEnabled && previousEnabledDay && (
													<Button
														type="button"
														label="Paste from above"
														icon="pi pi-copy"
														text
														className="machine-access-schedule-copy p-button-sm"
														aria-label={`Copy ${previousEnabledDay.label} hours to ${label}`}
														onClick={() => updateSchedule(key, value.orderingSchedule[previousEnabledDay.key])}
													/>
												)}
											</div>
											<InputText
												inputMode="numeric"
												maxLength={5}
												placeholder="HH:mm"
												aria-label={`${label} opening time`}
												value={opensAt}
												disabled={!dayEnabled}
												onChange={(event) => updateScheduleTime(key, 'opensAt', event.target.value)}
												className={validation.errors[`orderingSchedule.${key}`] ? 'p-invalid' : ''}
											/>
											<InputText
												inputMode="numeric"
												maxLength={5}
												placeholder="HH:mm"
												aria-label={`${label} closing time`}
												value={closesAt}
												disabled={!dayEnabled}
												onChange={(event) => updateScheduleTime(key, 'closesAt', event.target.value)}
												className={validation.errors[`orderingSchedule.${key}`] ? 'p-invalid' : ''}
											/>
											{validation.errors[`orderingSchedule.${key}`] && (
												<small className="p-error machine-access-schedule-error">
													{validation.errors[`orderingSchedule.${key}`]}
												</small>
											)}
										</div>
									);
								})}
								<small className="text-xs text-500 block mt-2">
									Switch a day off to keep ordering closed that day. An end time before the start time creates an
									overnight window; equal times allow ordering for 24 hours.
								</small>
							</div>
						)}
				</div>
			)}

			{!value.enabled && (
				<div className="machine-access-disabled-note mt-4">
					<i className="pi pi-info-circle"></i>
					<span>
						Saving while disabled keeps the technician and removes optional operator/user profiles from the active
						configuration.
					</span>
				</div>
			)}
		</div>
	);
};

export default MachineAccessSettings;
